import { css } from '@emotion/react';
import { hexColorWithOpacityToString } from '~/_shared/components/colorPicker/colorPicker.helpers';
import type { Theme } from '~/_shared/themes/theme.model';

const ARROW_HEIGHT = 20;

const centerStyle = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
});

export const mapOverlayPanelWrapperStyles = css({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  maxHeight: '100%',
  minHeight: 0,
  marginBlock: 'auto',
});

export const mapOverlayPanelBackgroundStyle = (theme: Theme) => css({
  backgroundColor: hexColorWithOpacityToString(theme.modalColors.contentBackground, .7),
  borderRadius: 10,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  height: 'auto',
  maxHeight: '100%',
  minHeight: 0,
  padding: 0,
  position: 'relative',
  boxShadow: `0px 0px 4px 2px rgba(0, 0, 0, ${theme.name === 'light' ? 0.4 : 0.6})`,
});

export const paddedSectionStyles = css({
  paddingBlock: 2,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  height: 'auto',
  maxHeight: '100%',
  minHeight: 0,

  '&:not(:first-of-type)': {
    paddingTop: 0,
  },
});

export const contentElementStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

export const contentScrollbarStyle = css({
  transitionProperty: 'height',
  transitionDuration: '.3s',
  transitionTimingFunction: 'ease-in-out',
});

export const arrowsWrapperStyle = css(centerStyle, {
  flexDirection: 'column',
  padding: '4px 0',
  width: '100%',
});

export const arrowStyle = (disabled?: boolean) => (theme: Theme) => css(centerStyle, {
  color: disabled ? theme.textColors.disabled : theme.textColors.secondary,
  fontSize: 32,
  height: ARROW_HEIGHT,
  width: '100%',
  background: 'transparent',
  border: 'none',

  '&:hover': {
    color: disabled ? theme.textColors.disabled : theme.textColors.info,
  },
});

export const dividerWrapperStyle = css({
  padding: '2px 4px',
  position: 'relative',
});

export const dividerStyle = (theme: Theme) => css({
  backgroundColor: theme.textColors.secondary,
  marginTop: -1,
});

const fadeCommon = (visible: boolean) => css({
  height: `${ARROW_HEIGHT}px`,
  left: 0,
  opacity: visible ? 1 : 0,
  pointerEvents: 'none',
  position: 'absolute',
  right: 0,
  zIndex: 1,
  transition: 'opacity 0.3s ease',
});

const getGradient = (direction: 'to bottom' | 'to top', theme: Theme) => css({
  background: `linear-gradient(${direction}, ${hexColorWithOpacityToString(theme.backgroundColors.secondary, 0.8)}, transparent)`,
});

export const fadeTopStyle = (visible: boolean, roundCorners: boolean) => (theme: Theme) => css([
  fadeCommon(visible),
  getGradient('to bottom', theme),
  { top: 0 },
  roundCorners ? {
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  } : null,
]);

export const fadeBottomStyle = (visible: boolean, roundCorners: boolean) => (theme: Theme) => css([
  fadeCommon(visible),
  getGradient('to top', theme),
  { bottom: 0 },
  roundCorners ? {
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  } : null,
]);
